import React from "react";
import { useTranslation } from "react-i18next";
import SignUpFormInput from "./SignUpFormInput";
import AvatarUpload from "../AvatarUpload";
import Button from "../../ui/Button";
import LoadingSpinner from "../../ui/LoadingSpinner";

/**
 * Sign Up Form Fields Component
 */
const SignUpFormFields = ({
  formData,
  formErrors,
  fieldValidation,
  isLoading,
  otpSent,
  showPassword,
  showConfirmPassword,
  handleChange,
  handleUsernameBlur,
  handleEmailBlur,
  handleImageChange,
  toggleShowPassword,
  toggleShowConfirmPassword,
  handleGetOtp,
}) => {
  const { t } = useTranslation("auth");

  return (
    <>
      {/* Username Input */}
      <SignUpFormInput
        name="username"
        type="text"
        placeholder={t("signUp.username")}
        value={formData.username}
        onChange={handleChange}
        onBlur={handleUsernameBlur}
        error={formErrors.username}
        validation={fieldValidation.username}
        loading={isLoading.checkingUsername}
        disabled={isLoading.submitting}
      />

      {/* Full Name Input */}
      <SignUpFormInput
        name="fullName"
        type="text"
        placeholder={t("signUp.fullName")}
        value={formData.fullName}
        onChange={handleChange}
        error={formErrors.fullName}
        disabled={isLoading.submitting}
      />

      {/* Email Input */}
      <SignUpFormInput
        name="email"
        type="email"
        placeholder={t("signUp.email")}
        value={formData.email}
        onChange={handleChange}
        onBlur={handleEmailBlur}
        error={formErrors.email}
        validation={fieldValidation.email}
        loading={isLoading.checkingEmail}
        disabled={isLoading.submitting}
      />

      {/* Profile Image Upload */}
      <AvatarUpload
        onImageChange={handleImageChange}
        currentImage={formData.profileImage}
        error={formErrors.profileImage}
        disabled={isLoading.submitting}
      />

      {/* Password Input */}
      <SignUpFormInput
        name="password"
        type={showPassword ? "text" : "password"}
        placeholder={t("signUp.password")}
        value={formData.password}
        onChange={handleChange}
        error={formErrors.password}
        showToggle={true}
        onToggleShow={toggleShowPassword}
        disabled={isLoading.submitting}
      />

      {/* Password requirements helper text */}
      <div className="text-xs text-gray-400 mt-1 pl-1 space-y-1">
        <div className="font-medium text-gray-300">
          {t("signUp.passwordRequirements")}
        </div>
        <ul className="space-y-0.5 ml-2">
          <li>• {t("signUp.passwordReq1")}</li>
          <li>• {t("signUp.passwordReq2")}</li>
          <li>• {t("signUp.passwordReq3")}</li>
          <li>• {t("signUp.passwordReq4")}</li>
          <li>• {t("signUp.passwordReq5")}</li>
        </ul>
      </div>

      {/* Confirm Password Input */}
      <SignUpFormInput
        name="confirmPassword"
        type={showConfirmPassword ? "text" : "password"}
        placeholder={t("signUp.confirmPassword")}
        value={formData.confirmPassword}
        onChange={handleChange}
        error={formErrors.confirmPassword}
        showToggle={true}
        onToggleShow={toggleShowConfirmPassword}
        disabled={isLoading.submitting}
      />

      {/* Get OTP Button */}
      <Button
        type="button"
        variant="outline"
        color="violet"
        size="lg"
        disabled={isLoading.sendingOtp || otpSent || isLoading.submitting}
        className="w-full flex items-center justify-center space-x-3"
        onClick={handleGetOtp}
      >
        {isLoading.sendingOtp ? (
          <>
            <LoadingSpinner size="sm" />
            <span>{t("signUp.sendingOtp")}</span>
          </>
        ) : otpSent ? (
          t("signUp.otpSent")
        ) : (
          t("signUp.getOtp")
        )}
      </Button>

      {/* OTP Input */}
      <SignUpFormInput
        name="otp"
        type="text"
        placeholder={t("signUp.otpPlaceholder")}
        value={formData.otp}
        onChange={handleChange}
        error={formErrors.otp}
        disabled={!otpSent || isLoading.submitting}
      />
    </>
  );
};

export default SignUpFormFields;
